import { gql } from '@apollo/client';

import {
  AddressFragment,
  UserFragment,
  BillingAccountFullFragment,
  ErrorFragment,
  OrderFragment,
  OneTimeProgramFragment,
  TransformationTribeFragment,
  NotificationPreferencesFragment,
  PrepaidPurchaseFragment,
  RateableOrderFragment,
  SubscriptionFragment,
  SubscriptionOptionsFragment,
  SubscriptionQuantityFragment,
  TastePreferencesFragment,
} from './fragments';

export const ACTIVATE_SUBSCRIPTION = gql`
  mutation ActivateSubscription($input: ActivateSubscriptionInput!) {
    activateSubscription(input: $input) {
      backgroundJob {
        id
      }
      purchase {
        startDay {
          id
          date
        }
      }
      errors {
        ...ErrorFragment
      }
    }
  }
  ${ErrorFragment}
`;

export const APPLY_PROMO_CODE = gql`
  mutation ApplyPromoCode($input: ApplyPromoCodeInput!) {
    applyPromoCode(input: $input) {
      user {
        id
      }
      errors {
        ...ErrorFragment
      }
    }
  }
  ${ErrorFragment}
`;

export const VALIDATE_CUSTOMER = gql`
  mutation ValidateCustomer($input: ValidateCustomerInput!) {
    validateCustomer(input: $input) {
      user {
        ...UserFragment
      }
      newUser
      errors {
        ...ErrorFragment
      }
    }
  }
  ${UserFragment}
  ${ErrorFragment}
`;

export const CANCEL_ORDER = gql`
  mutation CancelOrder($input: CancelOrderInput!) {
    cancelOrder(input: $input) {
      order {
        ...OrderFragment
      }
      errors {
        ...ErrorFragment
      }
    }
  }
  ${OrderFragment}
  ${ErrorFragment}
`;

export const COMPLETE_PREPAID_PURCHASE = gql`
  mutation CompletePrepaidPurchase($input: CompletePrepaidPurchaseInput!) {
    completePrepaidPurchase(input: $input) {
      backgroundJob {
        id
      }
      purchase {
        ...PrepaidPurchaseFragment
      }
      errors {
        ...ErrorFragment
      }
    }
  }
  ${PrepaidPurchaseFragment}
  ${ErrorFragment}
`;

export const COMPLETE_SPLIT_TEST_ORDER_PURCHASE = gql`
  mutation CompleteSplitTestOrderPurchase($input: CompleteSplitTestOrderPurchaseInput!) {
    completeSplitTestOrderPurchase(input: $input) {
      backgroundJob {
        id
      }
      errors {
        ...ErrorFragment
      }
    }
  }
  ${ErrorFragment}
`;

export const CREATE_INITIAL_ORDERS = gql`
  mutation CreateInitialOrders($input: CreateInitialOrdersInput!) {
    createInitialOrders(input: $input) {
      backgroundJob {
        id
      }
      errors {
        ...ErrorFragment
      }
    }
  }
  ${ErrorFragment}
`;

export const CREATE_ONE_TIME_PROGRAM = gql`
  mutation CreateOneTimeProgram($input: CreateOneTimeProgramInput!) {
    createOneTimeProgram(input: $input) {
      oneTimeProgram {
        ...OneTimeProgramFragment
      }
      errors {
        ...ErrorFragment
      }
    }
  }
  ${OneTimeProgramFragment}
  ${ErrorFragment}
`;

export const CREATE_TRANSFORMATION_TRIBE = gql`
  mutation CreateTransformationTribe($input: CreateTransformationTribeInput!) {
    createTransformationTribe(input: $input) {
      oneTimeProgram {
        ...TransformationTribeFragment
      }
      errors {
        ...ErrorFragment
      }
    }
  }
  ${TransformationTribeFragment}
  ${ErrorFragment}
`;

export const CREATE_ORDER = gql`
  mutation CreateOrder($input: CreateOrderInput!) {
    createOrder(input: $input) {
      order {
        ...OrderFragment
      }
      errors {
        ...ErrorFragment
      }
    }
  }
  ${OrderFragment}
  ${ErrorFragment}
`;

export const SET_DIET_AND_ALLERGIES_AND_CREATE_SUBSCRIPTION_QUANTITIES = gql`
  mutation SetDietAndAllergiesAndCreateSubscriptionQuantities(
    $setDietInput: SetDietInput!
    $setFoodsToAvoidInput: SetFoodsToAvoidInput!
    $createSubscriptionQuantitiesInput: CreateSubscriptionQuantitiesInput!
  ) {
    setDiet(input: $setDietInput) {
      user {
        id
        diet {
          id
        }
      }
      errors {
        ...ErrorFragment
      }
    }
    setFoodsToAvoid(input: $setFoodsToAvoidInput) {
      user {
        id
        ...TastePreferencesFragment
      }
      errors {
        ...ErrorFragment
      }
    }
    createSubscriptionQuantities(input: $createSubscriptionQuantitiesInput) {
      subscription {
        ...SubscriptionFragment
      }
      errors {
        ...ErrorFragment
      }
    }
  }
  ${SubscriptionFragment}
  ${TastePreferencesFragment}
  ${ErrorFragment}
`;

export const CREATE_SUBSCRIPTION_QUANTITIES = gql`
  mutation CreateSubscriptionQuantities($input: CreateSubscriptionQuantitiesInput!) {
    createSubscriptionQuantities(input: $input) {
      subscription {
        ...SubscriptionFragment
      }
      errors {
        ...ErrorFragment
      }
    }
  }
  ${SubscriptionFragment}
  ${ErrorFragment}
`;

export const RATE_PRODUCT_COMPONENT = gql`
  mutation RateProductComponent($input: RateProductComponentInput!) {
    rateProductComponent(input: $input) {
      order {
        ...RateableOrderFragment
      }
      errors {
        ...ErrorFragment
      }
    }
  }
  ${RateableOrderFragment}
  ${ErrorFragment}
`;

export const RESET_PASSWORD = gql`
  mutation ResetPassword($input: ResetPasswordInput!) {
    resetPassword(input: $input) {
      signedIn
      user {
        ...UserFragment
      }
      errors {
        ...ErrorFragment
      }
    }
  }
  ${UserFragment}
  ${ErrorFragment}
`;

export const SEND_RESET_PASSWORD_EMAIL = gql`
  mutation SendResetPasswordEmail($input: SendResetPasswordEmailInput!) {
    sendResetPasswordEmail(input: $input) {
      errors {
        ...ErrorFragment
      }
    }
  }
  ${ErrorFragment}
`;

export const SET_ACCOUNT_CREDENTIALS = gql`
  mutation SetAccountCredentials($input: SetAccountCredentialsInput!) {
    setAccountCredentials(input: $input) {
      user {
        id
        email
      }
      errors {
        ...ErrorFragment
      }
    }
  }
  ${ErrorFragment}
`;

export const SET_ACCOUNT_INFO = gql`
  mutation SetAccountInfo($input: SetAccountInfoInput!) {
    setAccountInfo(input: $input) {
      user {
        id
        firstName
        lastName
        email
        phoneNumber
      }
      errors {
        ...ErrorFragment
      }
    }
  }
  ${ErrorFragment}
`;

export const SET_BILLING_INFO = gql`
  mutation SetBillingInfo($input: SetBillingInfoInput!) {
    setBillingInfo(input: $input) {
      billingAccount {
        ...BillingAccountFullFragment
      }
      errors {
        ...ErrorFragment
      }
    }
  }
  ${BillingAccountFullFragment}
  ${ErrorFragment}
`;

export const SET_DELIVERY_INSTRUCTIONS = gql`
  mutation SetDeliveryInstructions($input: SetDeliveryInstructionsInput!) {
    setDeliveryInstructions(input: $input) {
      user {
        id
        primaryAddress {
          ...AddressFragment
        }
      }
    }
  }
  ${AddressFragment}
`;

export const SET_DIET_AND_ALLERGIES = gql`
  mutation SetDietAndAllergies(
    $setDietInput: SetDietInput!
    $setFoodsToAvoidInput: SetFoodsToAvoidInput!
  ) {
    setDiet(input: $setDietInput) {
      user {
        id
        diet {
          id
        }
      }
      errors {
        ...ErrorFragment
      }
    }
    setFoodsToAvoid(input: $setFoodsToAvoidInput) {
      user {
        id
        ...TastePreferencesFragment
      }
      errors {
        ...ErrorFragment
      }
    }
  }
  ${TastePreferencesFragment}
  ${ErrorFragment}
`;

export const SET_FOODS_TO_AVOID = gql`
  mutation SetFoodsToAvoid($input: SetFoodsToAvoidInput!) {
    setFoodsToAvoid(input: $input) {
      user {
        id
        ...TastePreferencesFragment
      }
      errors {
        ...ErrorFragment
      }
    }
  }
  ${TastePreferencesFragment}
  ${ErrorFragment}
`;

export const SET_GENDER_AND_MOTIVATION = gql`
  mutation SetGenderAndMotivation(
    $setGenderInput: SetGenderInput!
    $setMotivationInput: SetMotivationInput!
  ) {
    setGender(input: $setGenderInput) {
      user {
        id
        gender
      }
      errors {
        ...ErrorFragment
      }
    }
    setMotivation(input: $setMotivationInput) {
      user {
        id
        motivation {
          id
        }
      }
      errors {
        ...ErrorFragment
      }
    }
  }
  ${ErrorFragment}
`;

export const SET_PRODUCT_SIZE = gql`
  mutation SetProductSize($input: SetProductSizeInput!) {
    setProductSize(input: $input) {
      subscription {
        ...SubscriptionFragment
      }
      errors {
        ...ErrorFragment
      }
    }
  }
  ${SubscriptionFragment}
  ${ErrorFragment}
`;

export const SET_NOTIFICATION_PREFERENCES = gql`
  mutation SetNotificationPreferences($input: SetNotificationPreferencesInput!) {
    setNotificationPreferences(input: $input) {
      notificationPreferences {
        ...NotificationPreferencesFragment
      }
      errors {
        ...ErrorFragment
      }
    }
  }
  ${NotificationPreferencesFragment}
  ${ErrorFragment}
`;

export const SET_DELIVERY_INFO = gql`
  mutation SetDeliveryInfo($input: SetDeliveryInfoInput!) {
    setDeliveryInfo(input: $input) {
      user {
        id
      }
      errors {
        path
        message
      }
    }
  }
`;

export const SET_DELIVERY_INFO_AND_SMS_PREFERENCES = gql`
  mutation SetDeliveryInfoAndSmsPreferences(
    $setDeliveryInfoInput: SetDeliveryInfoInput!
    $setSmsPreferencesInput: SetSmsPreferencesInput!
  ) {
    setDeliveryInfo(input: $setDeliveryInfoInput) {
      user {
        id
        phoneNumber
        primaryAddress {
          ...AddressFragment
        }
      }
      errors {
        ...ErrorFragment
      }
    }
    setSmsPreferences(input: $setSmsPreferencesInput) {
      user {
        id
        notificationSettings {
          ...NotificationPreferencesFragment
        }
      }
      errors {
        ...ErrorFragment
      }
    }
  }
  ${AddressFragment}
  ${ErrorFragment}
  ${NotificationPreferencesFragment}
`;

export const SET_DELIVERY_INFO_AND_SMS_AND_ACCOUNT_TYPE_PREFERENCES = gql`
  mutation SetDeliveryInfoAndSmsPreferences(
    $setUserAccountInput: SetUserAccountInput!
    $setDeliveryInfoInput: SetDeliveryInfoInput!
    $setSmsPreferencesInput: SetSmsPreferencesInput!
  ) {
    setUserAccount(input: $setUserAccountInput) {
      user {
        ...UserFragment
      }
    }
    setDeliveryInfo(input: $setDeliveryInfoInput) {
      user {
        id
        phoneNumber
        primaryAddress {
          ...AddressFragment
        }
      }
      errors {
        ...ErrorFragment
      }
    }
    setSmsPreferences(input: $setSmsPreferencesInput) {
      user {
        id
        notificationSettings {
          ...NotificationPreferencesFragment
        }
      }
      errors {
        ...ErrorFragment
      }
    }
  }
  ${AddressFragment}
  ${ErrorFragment}
  ${NotificationPreferencesFragment}
  ${UserFragment}
`;

export const SET_DELIVERY_INFO_AND_SMS_AND_ACCOUNT_CREDIENTIALS = gql`
  mutation SetDeliveryInfoAndSmsPreferences(
    $setAccountCredentialsInput: SetAccountCredentialsInput!
    $setDeliveryInfoInput: SetDeliveryInfoInput!
    $setSmsPreferencesInput: SetSmsPreferencesInput!
  ) {
    setAccountCredentials(input: $setAccountCredentialsInput) {
      user {
        id
        email
      }
      errors {
        ...ErrorFragment
      }
    }
    setDeliveryInfo(input: $setDeliveryInfoInput) {
      user {
        id
        phoneNumber
        primaryAddress {
          ...AddressFragment
        }
      }
      errors {
        ...ErrorFragment
      }
    }
    setSmsPreferences(input: $setSmsPreferencesInput) {
      user {
        id
        notificationSettings {
          ...NotificationPreferencesFragment
        }
      }
      errors {
        ...ErrorFragment
      }
    }
  }
  ${AddressFragment}
  ${ErrorFragment}
  ${NotificationPreferencesFragment}
`;

export const SET_PRIMARY_ZIP_CODE = gql`
  mutation SetPrimaryZipCode($input: SetPrimaryZipCodeInput!) {
    setPrimaryZipCode(input: $input) {
      user {
        id
        primaryAddress {
          ...AddressFragment
        }
      }
      errors {
        ...ErrorFragment
      }
    }
  }
  ${AddressFragment}
  ${ErrorFragment}
`;

export const SET_SMS_PREFERENCES = gql`
  mutation SetSmsPreferences($input: SetSmsPreferencesInput!) {
    setSmsPreferences(input: $input) {
      user {
        id
      }
      errors {
        ...ErrorFragment
      }
    }
  }
  ${ErrorFragment}
`;

export const SET_PASSWORD = gql`
  mutation SetPassword($input: SetPasswordInput!) {
    setPassword(input: $input) {
      user {
        ...UserFragment
      }
      errors {
        ...ErrorFragment
      }
    }
  }
  ${UserFragment}
  ${ErrorFragment}
`;

export const SET_SPICY_PREFERENCE = gql`
  mutation SetSpicyPreference($input: SetSpicyPreferenceInput!) {
    setSpicyPreference(input: $input) {
      user {
        id
        ...TastePreferencesFragment
      }
      errors {
        ...ErrorFragment
      }
    }
  }
  ${TastePreferencesFragment}
  ${ErrorFragment}
`;

export const SET_SUBSCRIPTION_OPTIONS = gql`
  mutation SetSubscriptionOptions($input: SetSubscriptionOptionsInput!) {
    setSubscriptionOptions(input: $input) {
      subscriptionOptions {
        ...SubscriptionOptionsFragment
      }
      errors {
        ...ErrorFragment
      }
    }
  }
  ${SubscriptionOptionsFragment}
  ${ErrorFragment}
`;

export const SET_SUBSCRIPTION_QUANTITIES = gql`
  mutation SetSubscriptionQuantities($input: SetSubscriptionQuantitiesInput!) {
    setSubscriptionQuantities(input: $input) {
      subscription {
        id
        subscriptionQuantities {
          ...SubscriptionQuantityFragment
        }
      }
      errors {
        ...ErrorFragment
      }
    }
  }
  ${SubscriptionQuantityFragment}
  ${ErrorFragment}
`;

export const SIGN_IN = gql`
  mutation SignIn($input: SignInInput!) {
    signIn(input: $input) {
      signedIn
      errors {
        ...ErrorFragment
      }
      user {
        ...UserFragment
      }
    }
  }
  ${ErrorFragment}
  ${UserFragment}
`;

export const SIGN_OUT = gql`
  mutation SignOut($input: SignOutInput!) {
    signOut(input: $input) {
      signedIn
      errors {
        ...ErrorFragment
      }
    }
  }
  ${ErrorFragment}
`;

export const SIGN_UP = gql`
  mutation SignUp($input: SignUpInput!) {
    signUp(input: $input) {
      signedIn
      existingUser
      user {
        ...UserFragment
      }
      errors {
        ...ErrorFragment
      }
    }
  }
  ${UserFragment}
  ${ErrorFragment}
`;

export const SKIP_ORDER = gql`
  mutation SkipOrder($input: SkipOrderInput!) {
    skipOrder(input: $input) {
      order {
        ...OrderFragment
      }
      errors {
        ...ErrorFragment
      }
    }
  }
  ${OrderFragment}
  ${ErrorFragment}
`;

export const UNSKIP_ORDER = gql`
  mutation UnskipOrder($input: UnskipOrderInput!) {
    unskipOrder(input: $input) {
      order {
        ...OrderFragment
      }
      errors {
        ...ErrorFragment
      }
    }
  }
  ${OrderFragment}
  ${ErrorFragment}
`;

export const UPDATE_LINE_ITEM = gql`
  mutation UpdateLineItem($input: UpdateLineItemInput!) {
    updateLineItem(input: $input) {
      order {
        ...OrderFragment
      }
      errors {
        ...ErrorFragment
      }
    }
  }
  ${OrderFragment}
  ${ErrorFragment}
`;

export const UPSERT_ITEM_ON_ORDER = gql`
  mutation UpsertItemOnOrder($input: UpsertItemOnOrderInput!) {
    upsertItemOnOrder(input: $input) {
      order {
        ...OrderFragment
      }
      deliveryDay {
        id
        order {
          id
        }
      }
      errors {
        ...ErrorFragment
      }
    }
  }
  ${OrderFragment}
  ${ErrorFragment}
`;

export const SET_ZIPCODE_BY_EMAIL = gql`
  mutation SetZipcodeByEmail($input: SetZipcodeByEmailInput!) {
    setZipcodeByEmail(input: $input) {
      signedIn
      user {
        ...UserFragment
      }
      zipcodeType
      errors {
        path
        message
      }
    }
  }
  ${UserFragment}
`;

export const SET_USER_ACCOUNT_TYPE = gql`
  mutation SetUserAccount($input: SetUserAccountInput!) {
    setUserAccount(input: $input) {
      user {
        ...UserFragment
      }
    }
  }
  ${UserFragment}
`;

export const ADD_USER_MEAL_SWAP_REQUEST = gql`
  mutation AddUserMealSwapRequest($input: AddUserMealSwapRequestInput!) {
    addUserMealSwapRequest(input: $input) {
      success
      errors {
        message
        path
      }
    }
  }
`;

export const DESTROY_USER_MEAL_SWAP_REQUEST = gql`
  mutation DestroyUserMealSwapRequests($input: DestroyUserMealSwapRequestsInput!) {
    destroyUserMealSwapRequests(input: $input) {
      success
      destroyedCount
      errors {
        message
        path
      }
    }
  }
`;

export const CREATE_DELIVERY_SKIPS = gql`
  mutation CreateDeliverySkips($input: CreateDeliverySkipsInput!) {
    createDeliverySkips(input: $input) {
      skips {
        id
        createdAt
        deliveryPeriodId
      }
      hasPaidMealSwaps
      message
      errors {
        path
        message
      }
    }
  }
`;

export const DESTROY_DELIVERY_SKIPS = gql`
  mutation DestroyDeliverySkips($input: DestroyDeliverySkipsInput!) {
    destroyDeliverySkips(input: $input) {
      destroyedCount
      errors {
        path
        message
      }
    }
  }
`;

export const COMPLETE_USER_SWAP_PURCHASE = gql`
  mutation CompleteUserSwapPurchase($input: CompleteUserSwapPurchaseInput!) {
    completeUserSwapPurchase(input: $input) {
      backgroundJob {
        id
      }
      invoice {
        id
      }
      errors {
        path
        message
      }
    }
  }
`;

export const DELETE_OR_SET_PRIMARY_ADDRESS = gql`
  mutation DeleteOrSetPrimaryAddress($input: DeleteOrSetPrimaryAddressInput!) {
    deleteOrSetPrimaryAddress(input: $input) {
      success
      errors {
        path
        message
      }
    }
  }
`;

export const UPDATE_USER_OTP_SUBSCRIPTION_STATUS = gql`
  mutation UpdateUserOtpSubscriptionStatus($input: UpdateUserOtpSubscriptionStatusInput!) {
    updateUserOtpSubscriptionStatus(input: $input) {
      success
      errors {
        path
        message
      }
    }
  }
`;

export const SET_HEALTH_GOALS = gql`
  mutation SetHealthGoals($input: SetHealthGoalsInput!) {
    setHealthGoals(input: $input) {
      healthProfile {
        id
        healthGoals {
          id
          name
        }
      }
      errors {
        path
        message
      }
    }
  }
`;

export const CANCEL_SUBSCRIPTION = gql`
  mutation CancelSubscription($input: CancelSubscriptionInput!) {
    cancelSubscription(input: $input) {
      success
      errors {
        path
        message
      }
    }
  }
`;

export const SET_HEALTH_PROFILE = gql`
  mutation SetHealthProfile($input: SetHealthProfileInput!) {
    setHealthProfile(input: $input) {
      healthProfile {
        id
        activityLevel
        trainingType
        householdSize
        onGlp1Medication
        bloodworkMethod
      }
      errors {
        path
        message
      }
    }
  }
`;

export const PAUSE_SUBSCRIPTION = gql`
  mutation PauseSubscription($input: PauseSubscriptionInput!) {
    pauseSubscription(input: $input) {
      success
      errors {
        path
        message
      }
    }
  }
`;

export const SET_GENDER = gql`
  mutation SetGender($input: SetGenderInput!) {
    setGender(input: $input) {
      user {
        ...UserFragment
      }
      errors {
        ...ErrorFragment
      }
    }
  }
  ${UserFragment}
  ${ErrorFragment}
`;

export const SET_AGE_RANGE = gql`
  mutation SetAgeRange($input: SetAgeRangeInput!) {
    setAgeRange(input: $input) {
      user {
        ...UserFragment
      }
      errors {
        ...ErrorFragment
      }
    }
  }
  ${UserFragment}
  ${ErrorFragment}
`;

export const SET_FOOD_MOTIVATION = gql`
  mutation SetFoodMotivations($input: SetFoodMotivationsInput!) {
    setFoodMotivations(input: $input) {
      healthProfile {
        id
        foodMotivations {
          id
          name
        }
      }
      errors {
        ...ErrorFragment
      }
    }
  }
  ${ErrorFragment}
`;

export const SET_DIET = gql`
  mutation SetDiet($input: SetDietInput!) {
    setDiet(input: $input) {
      user {
        id
        diet {
          id
        }
      }
      errors {
        ...ErrorFragment
      }
    }
  }
  ${ErrorFragment}
`;

export const SET_CUISINE_PREFERENCES = gql`
  mutation SetCuisinePreferences($input: SetCuisinePreferencesInput!) {
    setCuisinePreferences(input: $input) {
      healthProfile {
        id
        cuisines {
          id
          name
        }
      }
      errors {
        ...ErrorFragment
      }
    }
  }
  ${ErrorFragment}
`;

export const SET_FAVORITE_PROTEINS = gql`
  mutation SetFavoriteProteins($input: SetFavoriteProteinsInput!) {
    setFavoriteProteins(input: $input) {
      healthProfile {
        id
      }
      errors {
        ...ErrorFragment
      }
    }
  }
  ${ErrorFragment}
`;

export const SET_BLOODWORK_MARKERS = gql`
  mutation SetBloodworkMarkers($input: SetBloodworkMarkersInput!) {
    setBloodworkMarkers(input: $input) {
      healthProfile {
        id
        bloodworkMarkers {
          status
          bloodworkMarker {
            id
            name
          }
        }
      }
      errors {
        ...ErrorFragment
      }
    }
  }
  ${ErrorFragment}
`;

export const CREATE_SPLIT_TEST_ASSIGNMENT = gql`
  mutation CreateSplitTestAssignment($input: CreateSplitTestAssignmentInput!) {
    createSplitTestAssignment(input: $input) {
      backgroundJob {
        id
      }
      errors {
        ...ErrorFragment
      }
    }
  }
  ${ErrorFragment}
`;

export const RESUME_SUBSCRIPTION = gql`
  mutation ResumeSubscription($input: ResumeSubscriptionInput!) {
    resumeSubscription(input: $input) {
      success
      errors {
        path
        message
      }
    }
  }
`;

export const PAUSE_OTP_SUBSCRIPTION = gql`
  mutation PauseOtpSubscription($input: PauseOtpSubscriptionInput!) {
    pauseOtpSubscription(input: $input) {
      success
      errors {
        path
        message
      }
    }
  }
`;

export const ACTIVATE_OTP_SUBSCRIPTION = gql`
  mutation ActivateOtpSubscription($input: ActivateOtpSubscriptionInput!) {
    activateOtpSubscription(input: $input) {
      success
      errors {
        path
        message
      }
    }
  }
`;

export const RECORD_TERMS_CONSENT = gql`
  mutation RecordTermsConsent($input: RecordTermsConsentInput!) {
    recordTermsConsent(input: $input) {
      termsConsent {
        id
        termsVersion
        consented
        createdAt
      }
      errors {
        ...ErrorFragment
      }
    }
  }
  ${ErrorFragment}
`;
